import { useEffect, useState } from 'react';
import { t } from '@app/i18n';
import {
  Badge,
  Button,
  Card,
  Chip,
  DataTable,
  DrawerPresence,
  IconButton,
  PAGE_SIZE,
  PageHeader,
  useToast,
  type DataTableColumn,
} from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { dataHour } from '@app/format';
import { useDrawerParam } from '@app/navigation';
import {
  useCreateUser,
  useDeactivateUser,
  useReactivateUser,
  useResetPassword,
  useUpdateUser,
  useUser,
  useUsers,
} from './Users.api';
import {
  FILTERS_INICIAIS,
  forCreate,
  forUpdate,
  hasFilterApplied,
  type StateFilter,
  type Role,
  type User,
  type UserFormValues,
  type UsersFilterValues,
  type UsersSort,
} from './UserModel';
import { UsersFilters } from './UsersFilters';
import { UserDrawer } from './UserDrawer';
import { UserConfirm, type UserConfirmacao } from './UserConfirm';
import { PasswordDialog } from './PasswordDialog';
import s from './Users.module.css';

/** Senha em exibição. Fechou, acabou: não há como reabrir (FR-109). */
type PasswordExibida = { usuario: string; senha: string; contexto: 'criado' | 'redefinida' };

// `pipeline` (a conta da esteira) em `neutral`, como o viewer: nenhum dos dois escreve pelo
// console, e um tom de alerta leria como problema numa conta que está certa.
const ROLE_TOM = {
  admin: 'primary',
  approver: 'success',
  viewer: 'neutral',
  pipeline: 'neutral',
} as const;

/**
 * T22 — Usuários (Story 12).
 *
 * Única tela exclusiva de `admin`: o item de menu já é omitido para os demais
 * (`navigation.ts`) e a rota é guardada por `RequireRole` — aqui dentro cada ação
 * ainda pergunta por operação do contrato (`podeOperar`), que é a mesma matriz
 * que o servidor aplica por rota.
 */
export function UsersPage() {
  const [filters, setFiltros] = useState<UsersFilterValues>(FILTERS_INICIAIS);
  const [confirmacao, setConfirmacao] = useState<UserConfirmacao | null>(null);
  const [senha, setSenha] = useState<PasswordExibida | null>(null);

  const drawer = useDrawerParam();
  const toast = useToast();

  const lista = useUsers(filters);
  const create = useCreateUser();
  const edit = useUpdateUser();
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const reset = useResetPassword();

  const canCreate = useCanOperar('createUser');
  const canEdit = useCanOperar('updateUser');
  const canDeactivate = useCanOperar('deactivateUser');
  const canReactivate = useCanOperar('reactivateUser');
  const canReset = useCanOperar('resetUserPassword');

  const itens = lista.data?.items ?? [];

  // Drawer aberto por URL (`?drawer=<uuid>`): se o registro não está na página
  // carregada, busca-se o usuário — a URL do painel é compartilhável.
  const editandoId = drawer.id && drawer.id !== 'novo' ? drawer.id : null;
  const list = editandoId ? itens.find((u) => u.id === editandoId) : undefined;
  const buscado = useUser(editandoId && !list ? editandoId : null);
  const inEdit = list ?? buscado.data;

  /**
   * O DRAWER DE EDIÇÃO SÓ MONTA COM O REGISTRO EM MÃOS (mesmo defeito da tela «Sites»,
   * 10/09, achado pela base de fidelidade do S-f).
   *
   * Esta tela já BUSCA o usuário quando ele não está na página carregada — o dado
   * chega, só chega tarde. E como `defaultValues` do `useForm` é lido no PRIMEIRO
   * render e a `key` não muda quando a resposta aparece, o que ficava era um «Editar
   * usuário» com o e-mail em placeholder e o papel no default, para sempre. Aqui o
   * caminho é VIVO: a trilha de auditoria linka para cá (`usecases/audit/links.go`,
   * `usuario` com `Drawer: true`), então era isto que via quem clicava numa entrada
   * de T23.
   *
   * Não montar é a resposta honesta: até haver o que pôr no formulário, a tela é a
   * lista. A alternativa — uma `key` que inclua «carregado» — mostraria por um
   * instante o formulário vazio que depois se preenche sozinho, e quem estivesse a ler
   * a tela nesse instante não saberia qual dos dois estados é o verdadeiro.
   */
  const drawerPronto = drawer.id === 'novo' || Boolean(inEdit);

  /**
   * Id que não existe (URL velha, usuário removido, `404` da busca): quando as DUAS
   * leituras terminaram e nenhuma trouxe o registro, o parâmetro morre. Sem isto a URL
   * fica a prometer um painel que nunca abre.
   */
  useEffect(() => {
    if (!editandoId) return;
    if (lista.isPending || buscado.isPending) return;
    if (!inEdit) drawer.fechar();
  }, [editandoId, lista.isPending, buscado.isPending, inEdit, drawer]);

  function swapFilters(mudanca: Partial<UsersFilterValues>) {
    // Qualquer filtro novo volta à primeira página: manter a 3 sobre outro
    // recorte mostraria uma página vazia sem explicação.
    setFiltros((current) => ({ ...current, page: 1, ...mudanca }));
  }

  function closeDrawer() {
    create.reset();
    edit.reset();
    drawer.fechar();
  }

  function aoSubmeter(valores: UserFormValues) {
    if (inEdit) {
      edit.mutate(
        { id: inEdit.id, body: forUpdate(valores) },
        {
          onSuccess: (usuario) => {
            toast.mostrar(t('users.toast.updated', { name: usuario.name }), {
              tone: 'sucesso',
            });
            closeDrawer();
          },
        },
      );
      return;
    }

    create.mutate(forCreate(valores), {
      onSuccess: (resultado) => {
        drawer.fechar();
        create.reset();
        if (resultado.initial_password) {
          setSenha({
            usuario: resultado.user.name,
            senha: resultado.initial_password,
            contexto: 'criado',
          });
        } else {
          toast.mostrar(t('users.toast.created', { name: resultado.user.name }), {
            tone: 'sucesso',
          });
        }
      },
    });
  }

  function confirm() {
    if (!confirmacao) return;
    const { tipo, usuario } = confirmacao;

    if (tipo === 'desativar') {
      deactivate.mutate(usuario.id, {
        onSuccess: () => {
          setConfirmacao(null);
          toast.mostrar(t('users.toast.deactivated', { name: usuario.name }), {
            tone: 'sucesso',
          });
        },
      });
      return;
    }
    if (tipo === 'reativar') {
      reactivate.mutate(usuario.id, {
        onSuccess: () => {
          setConfirmacao(null);
          toast.mostrar(t('users.toast.reactivated', { name: usuario.name }), {
            tone: 'sucesso',
          });
        },
      });
      return;
    }

    reset.mutate(usuario.id, {
      onSuccess: (resultado) => {
        setConfirmacao(null);
        setSenha({ usuario: usuario.name, senha: resultado.password, contexto: 'redefinida' });
      },
    });
  }

  function requestConfirmacao(tipo: UserConfirmacao['tipo'], usuario: User) {
    deactivate.reset();
    reactivate.reset();
    reset.reset();
    setConfirmacao({ tipo, usuario });
  }

  const columns: DataTableColumn<User>[] = [
    {
      key: 'name',
      header: t('users.list.column.name'),
      sortable: true,
      cell: (usuario) =>
        canEdit ? (
          <button type="button" className={s.nome} onClick={() => drawer.abrir(usuario.id)}>
            {usuario.name}
          </button>
        ) : (
          usuario.name
        ),
    },
    {
      key: 'email',
      header: t('users.list.column.email'),
      sortable: true,
      mono: true,
      cell: (u) => u.email,
    },
    {
      key: 'papel',
      header: t('users.list.column.role'),
      tip: t('users.list.column.role.tip'),
      // O papel na célula é o valor do contrato — não se traduz. O chip do Bypass
      // Approval mora ao lado dele: é o outro dado que diz o que esta pessoa pode.
      cell: (u) => (
        <span className={s.papel}>
          <Badge tone={ROLE_TOM[u.role]}>{u.role}</Badge>
          {u.auto_approve_requests ? (
            <span data-testid="user-bypass-approval">
              <Chip>{t('users.list.bypassApproval')}</Chip>
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'google',
      header: t('users.list.column.google'),
      tip: t('users.list.column.google.tip'),
      cell: (u) =>
        u.google_linked ? (
          <Chip icon="link">{t('users.list.google.linked')}</Chip>
        ) : (
          <span className={s.vazio}>—</span>
        ),
    },
    {
      key: 'estado',
      header: t('users.list.column.state'),
      cell: (u) =>
        u.active ? (
          <Badge tone="success">{t('users.list.state.active')}</Badge>
        ) : (
          <Badge tone="neutral" tip={t('users.list.state.inactive.tip')}>
            {t('users.list.state.inactive')}
          </Badge>
        ),
    },
    {
      key: 'ultimo_acesso',
      header: t('users.list.column.lastAccess'),
      cell: (u) =>
        dataHour(u.last_access_at) ?? (
          <span className={s.vazio}>{t('users.list.lastAccess.never')}</span>
        ),
    },
    {
      key: 'acoes',
      header: t('users.list.column.actions'),
      srOnlyHeader: true,
      width: '116px',
      cell: (usuario) => (
        <div className={s.acoes}>
          {canReset ? (
            <IconButton
              icon="key"
              label={t('users.list.action.resetPassword', { name: usuario.name })}
              onClick={() => requestConfirmacao('senha', usuario)}
            />
          ) : null}
          {usuario.active
            ? canDeactivate && (
                <IconButton
                  icon="ban"
                  danger
                  label={t('users.list.action.deactivate', { name: usuario.name })}
                  onClick={() => requestConfirmacao('desativar', usuario)}
                />
              )
            : canReactivate && (
                <IconButton
                  icon="check"
                  label={t('users.list.action.reactivate', { name: usuario.name })}
                  onClick={() => requestConfirmacao('reativar', usuario)}
                />
              )}
        </div>
      ),
    },
  ];

  const filtrado = hasFilterApplied(filters);

  return (
    <>
      <PageHeader
        title={t('users.list.title')}
        tip={t('users.list.purpose')}
        icon="users"
        badges={
          typeof lista.data?.total === 'number' ? (
            <Chip>{t('users.list.total', { n: lista.data.total })}</Chip>
          ) : null
        }
        actions={
          canCreate ? (
            <Button icon="plus" onClick={() => drawer.abrir('novo')}>
              {t('users.list.new')}
            </Button>
          ) : null
        }
      />

      <Card bare aria-label={t('users.list.card.label')}>
        <DataTable
          label={t('users.list.table.label')}
          columns={columns}
          rows={itens}
          rowKey={(u) => u.id}
          loading={lista.isLoading}
          error={lista.error}
          onRetry={() => void lista.refetch()}
          search={filters.q}
          onSearchChange={(q) => swapFilters({ q })}
          searchPlaceholder={t('users.list.search.placeholder')}
          filters={
            <UsersFilters
              role={filters.role}
              state={filters.state}
              onPapelChange={(role: Role | null) => swapFilters({ role })}
              onEstadoChange={(state: StateFilter) => swapFilters({ state })}
            />
          }
          filtersApplied={filtrado}
          onClearFilters={() => setFiltros(FILTERS_INICIAIS)}
          sort={{
            key: filters.sort.replace('-', ''),
            direction: filters.sort.startsWith('-') ? 'desc' : 'asc',
          }}
          onSortChange={({ key, direction }) =>
            swapFilters({ sort: `${direction === 'desc' ? '-' : ''}${key}` as UsersSort })
          }
          page={filters.page}
          pageSize={PAGE_SIZE}
          total={lista.data?.total}
          onPageChange={(page) => setFiltros((current) => ({ ...current, page }))}
          emptyIcon="users"
          emptyTitle={t(
            filtrado ? 'users.list.empty.filtered.title' : 'users.list.empty.none.title',
          )}
          emptyMessage={
            filtrado ? t('users.list.empty.filtered.message') : t('users.list.empty.none.message')
          }
          emptyAction={
            !filtrado && canCreate ? (
              <Button icon="plus" onClick={() => drawer.abrir('novo')}>
                {t('users.list.new')}
              </Button>
            ) : null
          }
        />
      </Card>

      <DrawerPresence>
        {drawer.id && drawerPronto ? (
          <UserDrawer
            // Recria o formulário a cada usuário aberto: `defaultValues` só é lido
            // no primeiro render do `useForm`.
            key={drawer.id}
            open
            usuario={inEdit}
            erro={inEdit ? edit.error : create.error}
            pending={inEdit ? edit.isPending : create.isPending}
            onSubmit={aoSubmeter}
            onClose={closeDrawer}
          />
        ) : null}
      </DrawerPresence>

      <UserConfirm
        confirmacao={confirmacao}
        pending={deactivate.isPending || reactivate.isPending || reset.isPending}
        erro={deactivate.error ?? reactivate.error ?? reset.error}
        onConfirm={confirm}
        onCancel={() => setConfirmacao(null)}
      />

      {senha ? (
        <PasswordDialog
          open
          usuario={senha.usuario}
          senha={senha.senha}
          contexto={senha.contexto}
          onClose={() => setSenha(null)}
        />
      ) : null}
    </>
  );
}
